import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import type { PrePlacementDocumentType } from '@duke-hq/svc-api-db';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';

interface RejectDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  documentType: PrePlacementDocumentType;
  onSuccess: () => void;
}

interface FormData {
  reviewNotes: string;
}

export function RejectDocumentModal({
  isOpen,
  onClose,
  journeyId,
  documentType,
  onSuccess,
}: RejectDocumentModalProps): React.JSX.Element {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { reviewNotes: '' } });

  const rejectMutation = trpcAdmin.workPlacements.placements.reviewDocument.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Document rejected` });
      reset();
      onSuccess();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to reject document`, description: error.message });
    },
  });

  const onSubmit = (data: FormData) => {
    rejectMutation.mutate({
      journeyId,
      documentType,
      action: 'REJECT',
      reviewNotes: data.reviewNotes,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t`Reject document`}
      body={
        <VStack align="stretch" spacing={4}>
          <FormControl isRequired isInvalid={!!errors.reviewNotes}>
            <FormLabel>
              <Trans>Reason for rejection</Trans>
            </FormLabel>
            <Textarea
              {...register('reviewNotes', {
                required: t`Please provide a reason for rejection`,
              })}
              placeholder={t`Explain why this document is being rejected`}
              rows={3}
            />
            {errors.reviewNotes ? (
              <FormErrorMessage>{errors.reviewNotes.message}</FormErrorMessage>
            ) : null}
          </FormControl>
        </VStack>
      }
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel={t`Reject`}
      isLoading={rejectMutation.isPending}
      variant="destructive"
    />
  );
}
